import { IconGripVertical } from '@rossum/ui/icons/tabler';
import { Stack, SvgIcon, SxProps, Theme } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { useTileDraggableContext } from './TileDraggableContext';

type TileDragHandleProps = {
  /** Force visibility regardless of hover state */
  visible?: boolean;
  /** Custom styles */
  sx?: SxProps<Theme>;
};

/**
 * Drag handle component for use within TileDraggable.
 * Automatically connects to drag context if available.
 * Renders nothing if used outside of TileDraggable context.
 */
export const TileDragHandle = ({ visible, sx }: TileDragHandleProps) => {
  const dragContext = useTileDraggableContext();

  // If no drag context, don't render the handle
  if (!dragContext) {
    return null;
  }

  const { handleProps, isActive, isPlaceholder } = dragContext;
  const showHandle = visible ?? (isActive || isPlaceholder);

  return (
    <Stack
      component="button"
      type="button"
      justifyContent="center"
      alignItems="center"
      {...handleProps}
      sx={{
        border: 'none',
        background: 'transparent',
        padding: 0,
        margin: 0,
        px: 0.5,
        alignSelf: 'stretch',

        // Visibility
        opacity: showHandle ? 1 : 0,

        // Cursor
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },

        // Transitions
        transition: t =>
          t.transitions.create(['opacity', 'background-color'], {
            duration: t.transitions.duration.short,
          }),

        // Focus/hover states
        outline: 'none',
        borderRadius: borderRadius('s'),
        '&:hover, &:focus-visible': {
          backgroundColor: t => t.palette.action.hover,
        },

        ...sx,
      }}
    >
      <SvgIcon fontSize="small" color="action" sx={{ opacity: 0.5 }}>
        <IconGripVertical />
      </SvgIcon>
    </Stack>
  );
};
